import { useEffect, useState } from 'react';

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Keeps a component mounted long enough to animate out.
 *
 * `mounted` says whether to render at all; `active` says whether to render in
 * the open visual state. On open they separate by two frames so the browser
 * paints the closed styles first and the transition has somewhere to start.
 * On close, `active` drops immediately and `mounted` follows once the exit
 * transition has had its duration.
 *
 * Honors prefers-reduced-motion by collapsing the delay to zero.
 */
export function usePresence(open: boolean, duration: number): { mounted: boolean; active: boolean } {
  const [mounted, setMounted] = useState(open);
  const [active, setActive] = useState(open);

  useEffect(() => {
    if (open) {
      setMounted(true);
      if (prefersReducedMotion()) {
        setActive(true);
        return;
      }
      let second = 0;
      const first = requestAnimationFrame(() => {
        second = requestAnimationFrame(() => setActive(true));
      });
      return () => {
        cancelAnimationFrame(first);
        cancelAnimationFrame(second);
      };
    }

    setActive(false);
    const ms = prefersReducedMotion() ? 0 : duration;
    const timer = window.setTimeout(() => setMounted(false), ms);
    return () => window.clearTimeout(timer);
  }, [open, duration]);

  return { mounted, active };
}
